// client/src/services/moodAnalyticsService.js - FIXED VERSION
import axios from 'axios';

// Setup base URL for API requests
const API_URL = process.env.REACT_APP_API_URL || '/api';

// Create axios instance with common configuration
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add request interceptor to include authentication token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Add response interceptor for better error logging
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Log errors to help with debugging
    console.error('Mood API Error:', {
      url: error.config?.url,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      message: error.message
    });
    return Promise.reject(error);
  }
);

// Service functions for mood analytics and doctor-related operations
const moodAnalyticsService = {
  /**
   * ✅ ENHANCED: Create a new mood check-in entry with NLP sentiment analysis
   */
  createMoodCheckIn: async (moodData) => {
    try {
      console.log('🏥 moodService.createMoodCheckIn called with:', moodData);
      const response = await api.post('/mood/checkin', moodData);
      console.log('✅ moodService.createMoodCheckIn response:', response.data);
      
      if (response.data.data?.nlpInsights) {
        console.log('🤖 NLP Analysis Results:', {
          sentiment: response.data.data.nlpInsights.detectedSentiment,
          riskLevel: response.data.data.nlpInsights.riskLevel,
          moodValidation: response.data.data.nlpInsights.moodValidation?.status,
        });
      }
      
      return response.data;
    } catch (error) {
      console.error('❌ moodService.createMoodCheckIn error:', error);
      throw error;
    }
  },

  /**
   * ✅ ENHANCED: Get mood history with sentiment analysis data
   */
  getMoodHistory: async (params = {}) => {
    try {
      const { limit = 30, page = 1, includeSentiment = true } = params;
      console.log('📊 moodService.getMoodHistory called with params:', { limit, page, includeSentiment });
      
      const response = await api.get('/mood/history', {
        params: { limit, page, includeSentiment }
      });
      console.log('✅ moodService.getMoodHistory response:', response.data);
      
      if (response.data.sentimentSummary) {
        console.log('🧠 Sentiment Summary:', response.data.sentimentSummary);
      }
      
      return response.data;
    } catch (error) {
      console.error('❌ moodService.getMoodHistory error:', error);
      throw error;
    }
  },

  /**
   * ✅ ENHANCED: Get mood analytics with sentiment insights
   */
  getMoodAnalytics: async (days = 30) => {
    try {
      console.log('📈 moodService.getMoodAnalytics called with days:', days);
      const response = await api.get('/mood/analytics', {
        params: { days }
      });
      console.log('✅ moodService.getMoodAnalytics response:', response.data);
      
      if (response.data.data?.sentimentAnalytics) {
        console.log('🧠 Sentiment Analytics:', response.data.data.sentimentAnalytics);
      }
      
      return response.data;
    } catch (error) {
      console.error('❌ moodService.getMoodAnalytics error:', error);
      throw error;
    }
  },

  /**
   * Get current/latest mood for authenticated user
   */
  getCurrentMood: async () => {
    try {
      console.log('📱 moodService.getCurrentMood called');
      const response = await api.get('/mood/current');
      console.log('✅ moodService.getCurrentMood response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ moodService.getCurrentMood error:', error);
      throw error;
    }
  },

  /**
   * ✅ ENHANCED: Check if user can submit a new mood check-in
   */
  canCheckIn: async () => {
    try {
      console.log('⏰ moodService.canCheckIn called');
      const response = await api.get('/mood/can-checkin');
      console.log('✅ moodService.canCheckIn response:', response.data);
      
      if (response.data.data?.lastCheckIn?.hadSentimentAnalysis) {
        console.log('🧠 Last check-in had sentiment analysis');
      }
      
      return response.data;
    } catch (error) {
      console.error('❌ moodService.canCheckIn error:', error);
      throw error;
    }
  },

  /**
   * ✅ ENHANCED: Get mood history for a specific user with clinical insights
   */
  getUserMoodHistory: async (userId, params = {}) => {
    try {
      const { limit = 30, page = 1, includeSentiment = true } = params;
      console.log('👨‍⚕️ moodService.getUserMoodHistory called for user:', userId, 'with params:', { limit, page, includeSentiment });
      
      const response = await api.get(`/mood/user/${userId}`, {
        params: { limit, page, includeSentiment }
      });
      console.log('✅ moodService.getUserMoodHistory response:', response.data);
      
      if (response.data.clinicalSummary) {
        console.log('🏥 Clinical Summary:', response.data.clinicalSummary);
      }
      
      return response.data;
    } catch (error) {
      console.error('❌ moodService.getUserMoodHistory error:', error);
      throw error;
    }
  },

  /**
   * Get mood analytics for a specific user (Admin/Doctor access)
   */
  getUserMoodAnalytics: async (userId, days = 30) => {
    try {
      console.log('📊 moodService.getUserMoodAnalytics called for user:', userId, 'with days:', days);
      const response = await api.get(`/mood/user/${userId}/analytics`, {
        params: { days }
      });
      console.log('✅ moodService.getUserMoodAnalytics response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ moodService.getUserMoodAnalytics error:', error);
      throw error;
    }
  },

  /**
   * ✅ ENHANCED: Get comprehensive mood analytics for all patients with sentiment insights
   */
  getDoctorPatientMoodAnalytics: async (days = 7) => {
    try {
      console.log('👨‍⚕️ moodService.getDoctorPatientMoodAnalytics called with days:', days);
      const response = await api.get('/mood/doctor/analytics', {
        params: { days }
      });
      console.log('✅ moodService.getDoctorPatientMoodAnalytics response:', response.data);
      
      if (response.data.data?.keyMetrics?.sentimentOverview) {
        console.log('🧠 Clinical Sentiment Overview:', response.data.data.keyMetrics.sentimentOverview);
      }
      
      return response.data;
    } catch (error) {
      console.error('❌ moodService.getDoctorPatientMoodAnalytics error:', error);
      throw error;
    }
  },

  /**
   * ✅ NEW: Get patient mood history data for the doctor dashboard table
   */
  getDoctorPatientMoodHistory: async (days = 7) => {
    try {
      console.log('👨‍⚕️ moodService.getDoctorPatientMoodHistory called with days:', days);
      const response = await api.get('/mood/doctor/history', {
        params: { days }
      });
      console.log('✅ moodService.getDoctorPatientMoodHistory response:', response.data);
      
      if (response.data.data && response.data.data.length > 0) {
        const highRiskPatients = response.data.data.filter(patient => 
          patient.sentimentSummary?.requiresAttention
        );
        
        if (highRiskPatients.length > 0) {
          console.log('🚨 High-risk patients requiring attention:', highRiskPatients.length);
        }
        
        console.log('📊 Patient sentiment summary:', {
          totalPatients: response.data.data.length,
          patientsWithSentiment: response.data.data.filter(p => p.sentimentSummary).length,
          highRiskCount: highRiskPatients.length
        });
      }
      
      return response.data;
    } catch (error) {
      console.error('❌ moodService.getDoctorPatientMoodHistory error:', error);
      throw error;
    }
  },

  /**
   * ✅ FIXED: Re-run sentiment analysis for a specific mood entry
   * @param {string} entryId - Mood entry ID
   * @returns {Promise} API response with updated sentiment analysis
   */
  rerunSentimentAnalysis: async (entryId) => {
    try {
      console.log('🔄 moodService.rerunSentimentAnalysis called for entry:', entryId);
      
      // ✅ FIXED: Correct endpoint path
      const response = await api.post(`/mood/entry/${entryId}/reanalyze`);
      console.log('✅ moodService.rerunSentimentAnalysis response:', response.data);
      
      if (response.data.data?.sentiment) {
        console.log('🤖 Updated Sentiment Analysis:', {
          type: response.data.data.sentiment.type,
          score: response.data.data.sentiment.score,
          riskLevel: response.data.data.sentiment.riskLevel,
          clinicalNotes: response.data.data.sentiment.clinicalNotes
        });
      }
      
      return response.data;
    } catch (error) {
      console.error('❌ moodService.rerunSentimentAnalysis error:', error);
      throw error;
    }
  },

  /**
   * ✅ ENHANCED: Get mood trends with sentiment analysis
   */
  getMoodTrends: async (userId = null, days = 30) => {
    try {
      const endpoint = userId ? `/mood/user/${userId}/analytics` : '/mood/analytics';
      console.log('📈 moodService.getMoodTrends called for endpoint:', endpoint);
      
      const response = await api.get(endpoint, {
        params: { days }
      });
      
      const analytics = response.data;
      if (analytics.success && analytics.data) {
        const trendData = {
          success: true,
          trends: analytics.data.trends || [],
          moodDistribution: analytics.data.moodDistribution || {},
          sentimentAnalytics: analytics.data.sentimentAnalytics || null,
          period: `${days} days`
        };
        
        if (trendData.sentimentAnalytics) {
          console.log('🧠 Sentiment Trends:', trendData.sentimentAnalytics.trend);
        }
        
        return trendData;
      }
      
      return response.data;
    } catch (error) {
      console.error('❌ moodService.getMoodTrends error:', error);
      throw error;
    }
  },

  /**
   * ✅ ENHANCED: Export mood data with sentiment analysis
   */
  exportMoodData: async (params = {}) => {
    try {
      const { 
        format = 'pdf', 
        userId = null, 
        days = 30,
        includeDetails = true,
        includeSentiment = true
      } = params;
      
      console.log('📄 moodService.exportMoodData called with params:', params);
      
      const endpoint = userId ? `/mood/user/${userId}/export` : '/mood/export';
      const response = await api.get(endpoint, {
        params: { 
          format, 
          days, 
          includeDetails,
          includeSentiment
        },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      const timestamp = new Date().toISOString().split('T')[0];
      const sentimentSuffix = includeSentiment ? '-with-sentiment' : '';
      const filename = `mood-analytics${sentimentSuffix}-${timestamp}.${format}`;
      link.setAttribute('download', filename);
      
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      return {
        success: true,
        message: `Mood data exported successfully as ${format.toUpperCase()}`,
        filename
      };
    } catch (error) {
      console.error('❌ moodService.exportMoodData error:', error);
      throw error;
    }
  },

  /**
   * ✅ NEW: Get high-risk patients requiring immediate attention
   */
  getHighRiskPatients: async (days = 7) => {
    try {
      console.log('🚨 moodService.getHighRiskPatients called with days:', days);
      const response = await api.get('/mood/doctor/high-risk', {
        params: { days }
      });
      console.log('✅ moodService.getHighRiskPatients response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ moodService.getHighRiskPatients error:', error);
      throw error;
    }
  },

  /**
   * ✅ NEW: Get sentiment analysis insights for a patient
   */
  getPatientSentimentInsights: async (userId, days = 30) => {
    try {
      console.log('🧠 moodService.getPatientSentimentInsights called for user:', userId, 'days:', days);
      const response = await api.get(`/mood/user/${userId}/sentiment`, {
        params: { days }
      });
      console.log('✅ moodService.getPatientSentimentInsights response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ moodService.getPatientSentimentInsights error:', error);
      throw error;
    }
  },

  /**
   * ✅ NEW: Get crisis intervention history
   */
  getCrisisHistory: async (days = 30) => {
    try {
      console.log('🚨 moodService.getCrisisHistory called with days:', days);
      const response = await api.get('/mood/doctor/crisis-history', {
        params: { days }
      });
      console.log('✅ moodService.getCrisisHistory response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ moodService.getCrisisHistory error:', error);
      throw error;
    }
  },

  /**
   * Get mood statistics summary
   */
  getMoodStatistics: async (params = {}) => {
    try {
      const { 
        userId = null,
        startDate = null,
        endDate = null,
        groupBy = 'day'
      } = params;
      
      console.log('📊 moodService.getMoodStatistics called with params:', params);
      
      const endpoint = userId ? `/mood/user/${userId}/statistics` : '/mood/statistics';
      const response = await api.get(endpoint, {
        params: { 
          startDate, 
          endDate, 
          groupBy 
        }
      });
      
      console.log('✅ moodService.getMoodStatistics response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ moodService.getMoodStatistics error:', error);
      throw error;
    }
  },

  /**
   * ✅ NEW: Get mood chart data formatted for visualization
   */
  getMoodChartData: async (params = {}) => {
    try {
      const { 
        userId = null,
        days = 30,
        chartType = 'mood'
      } = params;
      
      console.log('📈 moodService.getMoodChartData called with params:', params);
      
      const moodData = userId 
        ? await moodAnalyticsService.getUserMoodHistory(userId, { limit: 100, includeSentiment: true })
        : await moodAnalyticsService.getMoodHistory({ limit: 100, includeSentiment: true });
      
      if (!moodData.success || !moodData.data) {
        throw new Error('Failed to fetch mood data for chart');
      }
      
      const { formatChartData } = await import('../utils/moodUtils');
      const chartData = formatChartData(moodData.data, chartType);
      
      console.log('✅ moodService.getMoodChartData formatted chart data:', chartData);
      
      return {
        success: true,
        data: chartData,
        chartType,
        period: `${days} days`
      };
    } catch (error) {
      console.error('❌ moodService.getMoodChartData error:', error);
      throw error;
    }
  },

  // Additional utility methods for UI integration
  formatSentimentInsights: async (sentimentData) => {
    try {
      const { formatSentimentForClinicalDisplay } = await import('../utils/moodUtils');
      return formatSentimentForClinicalDisplay(sentimentData);
    } catch (error) {
      console.error('❌ moodService.formatSentimentInsights error:', error);
      throw error;
    }
  },

  getRiskLevelColor: async (riskLevel) => {
    try {
      const { getRiskLevelColor } = await import('../utils/moodUtils');
      return getRiskLevelColor(riskLevel);
    } catch (error) {
      console.error('❌ moodService.getRiskLevelColor error:', error);
      return '#9E9E9E';
    }
  },

  validateMoodData: async (moodData) => {
    try {
      const { validateMoodData } = await import('../utils/moodUtils');
      return validateMoodData(moodData);
    } catch (error) {
      console.error('❌ moodService.validateMoodData error:', error);
      return { isValid: false, errors: ['Validation failed'] };
    }
  },

  calculateMoodStatistics: async (moodData) => {
    try {
      const { calculateMoodStatistics } = await import('../utils/moodUtils');
      return calculateMoodStatistics(moodData);
    } catch (error) {
      console.error('❌ moodService.calculateMoodStatistics error:', error);
      throw error;
    }
  },

  formatMoodDataForExport: async (moodData, includeSentiment = true) => {
    try {
      const { formatMoodDataForExport } = await import('../utils/moodUtils');
      return formatMoodDataForExport(moodData, includeSentiment);
    } catch (error) {
      console.error('❌ moodService.formatMoodDataForExport error:', error);
      throw error;
    }
  },

  getMoodEmoji: async (moodKey) => {
    try {
      const { getMoodEmoji } = await import('../utils/moodUtils');
      return getMoodEmoji(moodKey);
    } catch (error) {
      console.error('❌ moodService.getMoodEmoji error:', error);
      return '😐';
    }
  }
};

export default moodAnalyticsService;